import { Fallback, HStack, VStack } from '@cbhq/cds-web/layout';
import {
  Link,
  TextHeadline,
  TextLabel1,
  TextLabel2,
  TextTitle1,
  TextTitle3,
  TextBody,
} from '@cbhq/cds-web/typography';
import { memo, Suspense, useCallback, useMemo } from 'react';
import { useAccount, useChainId } from 'wagmi';
import { useBreakpoints } from '@cbhq/cds-web/hooks/useBreakpoints';
import { usePoolsInfo } from '@/components/Pools/hooks/usePoolsInfo';
import { DisplayType, format } from '@/utils/numberFormatter';
import ErrorBoundary from '@/components/ErrorBoundary/ErrorBoundary';
import { ERROR_MESSAGE_POOLS_SUMMARY_FAILED } from '@/app/utils/errors';
import { useCompliance } from '@/components/ComplianceProvider/ComplianceProvider';

const SUMMARY_ITEMS = [
  { label: 'TVL', value: 0 },
  { label: '24H Volume', value: 0 },
  { label: '24H Fees', value: 0 },
];
const SUMMARY_CARD_TITLE = 'Overview';
const SUMMARY_ITEMS_GAP = 3;
const SUMMARY_ITEMS_FLEX_BASIS = `calc((100% - ${
  SUMMARY_ITEMS.length - 1
} * var(--spacing-${SUMMARY_ITEMS_GAP})) / ${SUMMARY_ITEMS.length})`;

function PoolsSummaryItemsLoading() {
  return (
    <>
      {SUMMARY_ITEMS.map((item) => {
        return (
          <VStack key={item.label} gap={1.5} flexBasis={SUMMARY_ITEMS_FLEX_BASIS} flexGrow={1}>
            <TextLabel1 as="span" noWrap>
              {item.label}
            </TextLabel1>
            <Fallback width="80%" height={36} />
          </VStack>
        );
      })}
    </>
  );
}

function PoolsSummaryItems() {
  const chainId = useChainId();

  const {
    data: { poolsInfo },
  } = usePoolsInfo({ chainId });

  const summaryItems = useMemo(
    () =>
      SUMMARY_ITEMS.map(({ label }) => {
        const notionalKey =
          label === 'TVL'
            ? ('tvlNotional' as const)
            : label === '24H Volume'
            ? ('volume24hNotional' as const)
            : ('fees24hNotional' as const);

        const notionalValue =
          poolsInfo?.reduce((acc, curr) => {
            acc += curr[notionalKey];
            return acc;
          }, 0) ?? 0;

        const value = format({ value: notionalValue, type: DisplayType.FiatShort });

        return {
          label,
          value,
        };
      }),
    [poolsInfo],
  );

  return (
    <>
      {summaryItems.map((item) => {
        return (
          <VStack key={item.label} gap={1.5} flexBasis={SUMMARY_ITEMS_FLEX_BASIS} flexGrow={1}>
            <TextHeadline as="span" noWrap color="fgMuted">
              {item.label}
            </TextHeadline>
            <TextTitle1 as="span">{item.value}</TextTitle1>
          </VStack>
        );
      })}
    </>
  );
}

const PoolsSummaryContent = memo(function PoolsSummaryContent() {
  const { isPhone, isPhoneLandscape, isTablet } = useBreakpoints();
  const isMobile = isPhone || isPhoneLandscape || isTablet;
  return (
    <>
      <VStack gap={1} paddingX={3}>
        <HStack justifyContent="space-between" alignItems="center">
          <TextTitle3 as="h1" testID="pools-title">
            {SUMMARY_CARD_TITLE}
          </TextTitle3>
        </HStack>
      </VStack>
      <VStack padding={SUMMARY_ITEMS_GAP} paddingBottom={0}>
        <VStack gap={SUMMARY_ITEMS_GAP} flexDirection={isMobile ? 'row' : 'column'}>
          <Suspense fallback={<PoolsSummaryItemsLoading />}>
            <PoolsSummaryItems />
          </Suspense>
        </VStack>
      </VStack>
    </>
  );
});

const PoolsSummaryWithErrorBoundary = memo(function PoolsSummaryWithErrorBoundary() {
  return (
    <VStack background="bg" borderRadius={300} paddingY={3} gap={3}>
      <ErrorBoundary message={ERROR_MESSAGE_POOLS_SUMMARY_FAILED}>
        <PoolsSummaryContent />
      </ErrorBoundary>
    </VStack>
  );
});

export const PoolsSummary = memo(function PoolsSummary() {
  return <PoolsSummaryWithErrorBoundary />;
});
